import React, { useCallback, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "lib/i18n/use-translation";
import { Button, ScoreSlider } from "components/ui";
import { ReactComponent as AssessmentIcon } from "Assets/icon/clipBoardIcon.svg";

import styles from "./stat-cards.module.scss";
import { BaseScore } from "apps/user/learner/api/types";
import { useInViews } from "utility/useInViews";
import { trackEvent } from "utility/mixpanel";
import { localToday, parseCalendarDate } from "utility/date";

interface IndexScoreCardProps {
  overallIndexScore?: BaseScore;
  takenAt?: string;
  summary?: string;
  onBreakdownClick?: () => void;
  onExplanationClick?: () => void;
}

const IndexScoreCard: React.FC<IndexScoreCardProps> = ({
  overallIndexScore,
  takenAt,
  summary,
  onBreakdownClick,
  onExplanationClick,
}) => {
  // Use the provided data or fallback values
  const scoreValue = overallIndexScore?.value;
  const scoreLabel = overallIndexScore?.label;
  // Display uses scoreLabel; analytics uses the band, which is locale-stable.
  const scoreBand = overallIndexScore?.labelKey;
  const { ref, inView } = useInViews();
  const location = useLocation();
  const { t } = useTranslation(["components", "index"]);

  const logImpression = useCallback(() => {
    trackEvent("Index Score Card Impression", {
      "Index Score": scoreValue ?? null,
      "Score Band": scoreBand ?? null,
      "Has Score": Boolean(scoreValue),
    });
  }, [scoreBand, scoreValue]);

  useEffect(() => {
    if (inView) {
      logImpression();
    }
  }, [inView, logImpression]);

  // takenAt is a midnight-UTC calendar date from the backend, so parse it
  // as a civil date to avoid an off-by-one for users in UTC− timezones.
  const formatDate = (dateString?: string) =>
    (dateString ? parseCalendarDate(dateString) : localToday()).format(
      "DD/MM/YY"
    );

  const formattedDate = formatDate(takenAt);

  if (!takenAt) {
    return null;
  }

  const handleBreakdownClick = () => {
    trackEvent("Index Score Card Clicked", {
      "Index Score": scoreValue ?? null,
      "Score Band": scoreBand ?? null,
      "Has Score": Boolean(scoreValue),
      Destination: location.pathname,
      "Current Page": window.location.href,
    });
    onBreakdownClick && onBreakdownClick();
  };

  const handleExplanationClick = () => {
    trackEvent("Index Explanation Clicked", {
      Source: "Index Score Card",
      "Button Label": "How the Index works",
      "Article ID": "58",
      Destination: "/user/articles/58",
    });
    onExplanationClick && onExplanationClick();
  };

  return (
    <div className={styles["index-score-card"]} ref={ref}>
      <div className={styles["index-score-header"]}>
        <div className={styles["index-score-title-container"]}>
          <AssessmentIcon width={24} height={24} color={styles.white} />
          <span className={styles["index-score-title"]}>
            {t("index:screen.title")}
          </span>
        </div>
        <span className={styles["index-score-date"]}>{formattedDate}</span>
      </div>

      <div className={styles["index-score-main"]}>
        <div className={styles["index-score-values-container"]}>
          <div className={styles["index-score-value"]}>{scoreValue}</div>
          <div className={styles["index-score-label"]}>{scoreLabel}</div>
        </div>

        <div className={styles["index-score-slider-container"]}>
          <ScoreSlider
            value={scoreValue}
            barColor="white"
            textColor="white"
            showScore={false}
          />
          <p className={styles["index-score-description"]}>{summary}</p>
        </div>

        <div className={styles["index-score-button-container"]}>
          <Button
            variant="enclosed-primary"
            size="medium"
            monochromeColour="white"
            onClick={handleBreakdownClick}
            className={styles["index-score-button"]}
          >
            {t("components:indexScoreCard.exploreResults")}
          </Button>
          <Button
            variant="enclosed-secondary"
            size="medium"
            monochromeColour="white"
            className={styles["index-score-button"]}
            onClick={handleExplanationClick}
          >
            {t("components:indexScoreCard.howIndexWorks")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default IndexScoreCard;
